import { Col, Row } from '@cavilha/layout';
import TextInput from '@cavilha/text-input';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import Button from '@cavilha/button';
import toMonetary from 'helpers/monetary';
import { useEffect, useState } from 'react';
import { connectRange } from 'react-instantsearch-dom';
import Box from '@cavilha/box';
import { useListFilter } from '@hooks/algolia/useListFilter';

type RangeInputProps = {
  unit?: string;
  currentRefinement: any;
  min: number;
  max: number;
  refine: Function;
  inputs: { rangeInputs: any; setRangeInputs: any };
  attribute: string;
};

function applyPlaceholderUnit(value: string, unit = 'size') {
  if (unit !== 'size') {
    return toMonetary(value, true);
  }

  return `${value} cm`;
}

const RangeInput = connectRange(
  ({
    unit = '',
    currentRefinement,
    min: attributeMin,
    max: attributeMax,
    refine,
    inputs: { rangeInputs, setRangeInputs },
    attribute,
  }: RangeInputProps) => {
    const [minInputValue, setMinInputValue] = useState('');
    const [maxInputValue, setMaxInputValue] = useState('');
    const [minHasError, setMinHasError] = useState(false);
    const [maxHasError, setMaxHasError] = useState(false);

    const { trackListFilter } = useListFilter();

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const regex = /^\d*([,.]\d{0,2})?$/;

      if (regex.test(e.target.value)) {
        setMinInputValue(e.target.value.replace('.', ','));
      }
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const regex = /^\d*([,.]\d{0,2})?$/;

      if (regex.test(e.target.value)) {
        setMaxInputValue(e.target.value.replace('.', ','));
      }
    };

    const handleSubmit = () => {
      const nextRangeInputs = {
        ...rangeInputs,
        ...{ [attribute]: { min: minInputValue, max: maxInputValue } },
      };

      setMinHasError(false);
      setMaxHasError(false);
      setRangeInputs(nextRangeInputs);

      let minToApply = attributeMin;
      let maxToApply = attributeMax;
      let refineToApply = {};

      if (minInputValue !== '') {
        const parsedMinValue = parseFloat(minInputValue.replace(',', '.'));

        if (parsedMinValue >= attributeMin && parsedMinValue <= attributeMax) {
          minToApply = parsedMinValue;
          refineToApply = { ...refineToApply, min: minToApply };
        }
      }

      if (maxInputValue !== '') {
        const parsedMaxValue = parseFloat(maxInputValue.replace(',', '.'));

        if (parsedMaxValue >= attributeMin && parsedMaxValue <= attributeMax) {
          maxToApply = parsedMaxValue;
          refineToApply = { ...refineToApply, max: maxToApply };
        }
      }

      trackListFilter({
        typeName: attribute,
        elementPosition: '1 e 2',
        elementName: `de ${minToApply} até ${maxToApply}`,
      });
      refine(refineToApply);
    };

    useEffect(() => {
      if (currentRefinement.min === attributeMin) {
        setMinInputValue('');
      }

      if (currentRefinement.max === attributeMax) {
        setMaxInputValue('');
      }
    }, [currentRefinement, attributeMin, attributeMax]);

    return (
      <Row>
        <Col sm={5} md={4} lg={5}>
          <Box
            css={{
              label: {
                background: '$neutral1',
                transform: 'translateY(-31px) scale(0.75) !important',
              },
            }}
          >
            <TextInput
              as="input"
              label="De"
              placeholder={applyPlaceholderUnit(`${attributeMin}`, unit)}
              name="inputminInputvalue"
              onChange={handleMinChange}
              value={minInputValue}
              floatingLabel
              hasError={minHasError}
            />
          </Box>
        </Col>
        <Col sm={5} md={4} lg={5}>
          <Box
            css={{
              label: {
                background: '$neutral1',
                transform: 'translateY(-31px) scale(0.75) !important',
              },
            }}
          >
            <TextInput
              as="input"
              label="Até"
              placeholder={applyPlaceholderUnit(`${attributeMax}`, unit)}
              name="inputmaxInputvalue"
              onChange={handleMaxChange}
              value={maxInputValue}
              floatingLabel
              hasError={maxHasError}
            />
          </Box>
        </Col>
        <Col sm={2} md={1} lg={1}>
          <Button
            css={{
              color: '$alwaysWhite',
              '@sm': { marginLeft: '0px', width: 'auto' },
              marginLeft: '-10px',
              width: '30px',

              '& i': {
                mr: '$none',
              },
            }}
            type="button"
            onClick={handleSubmit}
            alignIcon="left"
            variant="primary"
            icon={faCheck}
          />
        </Col>
      </Row>
    );
  }
);

export default RangeInput;
